import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  dismissWhatsNewAnnouncement,
  getActiveWhatsNewAnnouncement,
  type WhatsNewAnnouncement,
  whatsNewAnnouncements,
} from "@/components/whats-new/whats-new-announcements";
import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useState, useSyncExternalStore } from "react";

interface WhatsNewAnnouncementDialogProps {
  announcements?: WhatsNewAnnouncement[];
}

export const WhatsNewAnnouncementDialog = ({
  announcements = whatsNewAnnouncements,
}: WhatsNewAnnouncementDialogProps) => {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const hasHydrated = useSyncExternalStore(
    () => () => undefined,
    () => true,
    () => false,
  );
  const [, setDismissalCount] = useState(0);

  if (!hasHydrated) {
    return null;
  }

  const activeAnnouncement = getActiveWhatsNewAnnouncement(announcements, pathname);

  const dismissAnnouncement = () => {
    if (activeAnnouncement === null) {
      return;
    }

    dismissWhatsNewAnnouncement(activeAnnouncement);
    setDismissalCount((currentValue) => currentValue + 1);
  };

  return (
    <Dialog open={activeAnnouncement !== null} onOpenChange={(open) => !open && dismissAnnouncement()}>
      {activeAnnouncement !== null && (
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Sparkles className="size-5" aria-hidden="true" />
            </div>
            <DialogTitle>{activeAnnouncement.title}</DialogTitle>
            <DialogDescription>{activeAnnouncement.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            {activeAnnouncement.primaryAction && (
              <Button asChild onClick={dismissAnnouncement}>
                <Link to={activeAnnouncement.primaryAction.to}>
                  {activeAnnouncement.primaryAction.label}
                  <ArrowRight className="ml-2 size-4" aria-hidden="true" />
                </Link>
              </Button>
            )}
            <Button type="button" variant="outline" onClick={dismissAnnouncement}>
              <Check className="mr-2 size-4" aria-hidden="true" />
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};
